import type { Cajon, Prioridad, Confianza, Asunto } from '@/api/asuntos.api';

/**
 * EL VOCABULARIO VISUAL DE ATHENA.
 *
 * Todo lo que se repite en más de una pantalla vive acá. Es la única forma de
 * que el mandato se cumpla de verdad: *"Ninguna información sale repetida en
 * dos pantallas con diseños distintos."* Con los colores y los textos en cada
 * archivo, dos pantallas se separan en la tercera semana sin que nadie lo note.
 *
 * ═══ ESPAÑOL DOMINICANO, CON TÚ ═══
 *
 * Nada de voseo. Es una regla dura del mandato, y no es un capricho: la app le
 * habla a una persona concreta en Santo Domingo, y "arrastrá el bloque" suena
 * a otro país.
 */

/** El color del cajón. Sirve para ubicarse sin leer. */
export const CAJON_COLOR: Record<Cajon, { borde: string; chip: string; punto: string }> = {
  TRABAJO: {
    borde: 'border-l-blue-500',
    chip: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200',
    punto: 'bg-blue-500',
  },
  DINERO: {
    borde: 'border-l-emerald-500',
    chip: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200',
    punto: 'bg-emerald-500',
  },
  CLIENTES: {
    borde: 'border-l-violet-500',
    chip: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-200',
    punto: 'bg-violet-500',
  },
  PERSONAL: {
    borde: 'border-l-slate-600',
    chip: 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
    punto: 'bg-slate-600',
  },
};

export const CAJON_NOMBRE: Record<Cajon, string> = {
  TRABAJO: 'Trabajo',
  DINERO: 'Dinero',
  CLIENTES: 'Clientes',
  PERSONAL: 'Personal',
};

export const PRIORIDAD_NOMBRE: Record<Prioridad, string> = {
  ALTA: 'Urgente',
  MEDIA: 'Importante',
  BAJA: 'Puede esperar',
  RUIDO: 'Ruido',
  SIN_REVISAR: 'Sin revisar',
};

export const PRIORIDAD_TONO: Record<Prioridad, string> = {
  ALTA: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200',
  MEDIA: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200',
  BAJA: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
  RUIDO: 'bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500',
  SIN_REVISAR: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-200',
};

export const CONFIANZA_NOMBRE: Record<Confianza, string> = {
  SEGURO: 'Seguro',
  DUDOSO: 'Dudoso',
};

/** Cómo se llama cada tipo de asunto, en cristiano. */
export const TIPO_NOMBRE: Record<string, string> = {
  LLAMAR: 'Llamar',
  ESCRIBIR: 'Escribir',
  ENVIAR_INFO: 'Mandar algo',
  COTIZACION: 'Cotización',
  SEGUIMIENTO: 'Seguimiento',
  RETOMAR: 'Retomar',
  OTRO: 'Pendiente',
};

/**
 * La hora en formato 12 h, como pide el mandato: "3:00 p. m.".
 *
 * `toLocaleTimeString` con es-DO devuelve "3:00 p. m." en navegadores nuevos y
 * "3:00 PM" en algunos viejos, así que se normaliza a mano: la mezcla de dos
 * formatos en la misma lista se lee como un error.
 */
export function hora12(iso: string | Date): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  let h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, '0');
  const sufijo = h < 12 ? 'a. m.' : 'p. m.';
  h = h % 12 || 12;
  return `${h}:${m} ${sufijo}`;
}

export function fechaCorta(iso: string | Date): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  return d.toLocaleDateString('es-DO', { weekday: 'short', day: '2-digit', month: 'short' });
}

export function fechaLarga(iso: string | Date): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  return d.toLocaleDateString('es-DO', { weekday: 'long', day: 'numeric', month: 'long' });
}

/**
 * Plata, con su moneda. RD$ por defecto; US$ sólo cuando el mensaje lo dijo.
 *
 * Se guarda en centavos justamente para poder formatear acá sin arrastrar los
 * errores de redondeo de los flotantes.
 */
export function plata(centavos: number | null, moneda: string | null): string {
  if (centavos === null || centavos === undefined) return '';
  const simbolo = moneda === 'USD' ? 'US$' : 'RD$';
  return `${simbolo}${(centavos / 100).toLocaleString('es-DO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * Suma montos AGRUPANDO POR MONEDA, y los devuelve ya formateados.
 *
 * Existe por un error que se vio en la primera captura de «Hoy»: la tarjeta
 * decia "Te deben RD$63,320.00" sumando 45.000 pesos + 18.000 pesos + 320
 * DOLARES. Sumar monedas distintas no da un numero mas grande: da un numero
 * falso, y este es el que el dueno usa para decidir a quien cobrarle.
 *
 * Devuelve una lista porque puede haber dos: "RD$63,000.00" y "US$320.00" son
 * dos verdades, no una.
 */
export function plataPorMoneda(
  items: Array<{ montoCentavos: number | null; moneda: string | null }>,
): string[] {
  const suma = new Map<string, number>();
  for (const i of items) {
    if (i.montoCentavos === null || i.montoCentavos === undefined) continue;
    const m = i.moneda ?? 'DOP';
    suma.set(m, (suma.get(m) ?? 0) + i.montoCentavos);
  }
  // Los pesos primero: es la moneda de la casa.
  return [...suma.entries()]
    .sort((a, b) => (a[0] === 'DOP' ? -1 : b[0] === 'DOP' ? 1 : a[0].localeCompare(b[0])))
    .map(([moneda, centavos]) => plata(centavos, moneda));
}

/** Cuánto hace, en palabras. "hace 3 h", "hace 2 días". */
export function haceCuanto(iso: string | Date, ahora: Date = new Date()): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  const min = Math.floor((ahora.getTime() - d.getTime()) / 60_000);
  if (min < 1) return 'ahora mismo';
  if (min < 60) return `hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `hace ${h} h`;
  const dias = Math.floor(h / 24);
  return `hace ${dias} ${dias === 1 ? 'día' : 'días'}`;
}

/** En cuánto falta. "en 15 min", "mañana". */
export function enCuanto(iso: string | Date, ahora: Date = new Date()): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  const min = Math.round((d.getTime() - ahora.getTime()) / 60_000);
  if (min < 0) return haceCuanto(d, ahora);
  if (min < 60) return `en ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `en ${h} h`;
  const dias = Math.round(h / 24);
  return dias === 1 ? 'mañana' : `en ${dias} días`;
}

/**
 * Abre el chat en WhatsApp. **No envía nada.**
 *
 * Regla dura: la app nunca manda un mensaje. Esto arma el enlace `wa.me` y lo
 * abre; escribir y enviar lo hace el dueño, en WhatsApp. Con texto, WhatsApp
 * lo deja escrito en la caja sin enviarlo — sigue haciendo falta tocar enviar.
 */
export function enlaceWhatsApp(telefono: string | null, texto?: string): string | null {
  const limpio = (telefono ?? '').replace(/\D/g, '');
  if (limpio.length < 8) return null;
  const base = `https://wa.me/${limpio}`;
  return texto ? `${base}?text=${encodeURIComponent(texto)}` : base;
}

/**
 * Las dos letras del avatar.
 *
 * Salta los prefijos: "DEMO · Yanibel Almonte" daba "DE" para las cinco filas
 * de la demo, que es un avatar que no distingue a nadie. Y toma la inicial del
 * nombre Y del apellido cuando hay dos palabras.
 */
export function iniciales(nombre: string): string {
  const limpio = nombre
    .replace(/^DEMO\s*[·.-]\s*/i, '')
    .replace(/[(].*?[)]/g, '')
    .trim();
  const partes = limpio.split(/\s+/).filter(Boolean);
  if (partes.length === 0) return '?';
  if (partes.length === 1) return partes[0]!.slice(0, 2).toUpperCase();
  return (partes[0]![0]! + partes[1]![0]!).toUpperCase();
}

/**
 * El nombre sin el prefijo de demo, para las listas donde el espacio importa.
 * La marca DEMO sigue estando: la lleva el chip del canal y la hoja del asunto.
 */
export function nombreCorto(nombre: string): string {
  return nombre.replace(/^DEMO\s*[·.-]\s*/i, '').trim() || nombre;
}

/** ¿Este asunto está abierto para el dueño? */
export function estaAbierto(a: Asunto): boolean {
  return !['CUMPLIDO', 'DESCARTADO'].includes(a.estado);
}
